import plur from 'plur';

/**
 * Format a decimal value as a percentage with optional precision.
 * Removes trailing zeros to match d3-format behavior.
 *
 * @param value - The decimal value to format (e.g., 0.98 for 98%)
 * @param precision - Number of decimal places (default: 1)
 * @returns Formatted percentage string (e.g., "98%" or "<0.1%")
 *
 * @example
 * formatPercent(0.98, 1) // "98%"
 * formatPercent(0.981, 1) // "98.1%"
 * formatPercent(0.0005, 1) // "<0.1%"
 * formatPercent(0.00005, 2) // "<0.01%"
 */
export function formatPercent(value: number, precision = 1): string {
  const percent = value * 100;
  const threshold = Math.pow(10, -precision);

  // Handle values that round to 0 but aren't actually 0
  if (percent > 0 && percent < threshold) {
    // Format the threshold without trailing zeros
    const formattedThreshold = Number(threshold.toFixed(precision)).toString();
    return `<${formattedThreshold}%`;
  }

  // Format and remove trailing zeros
  const formatted = percent.toFixed(precision);
  const withoutTrailingZeros = Number(formatted).toString();
  return `${withoutTrailingZeros}%`;
}

/**
 * Format a decimal value as a percentage with a fixed number of decimals, the
 * way d3-format's `.1%` writes it (and so the way Ember's `pct` helper does):
 * the value times 100 through `toFixed`, keeping insignificant zeros and
 * grouping no digits. It rounds the float product, as `toFixed` does, which is
 * not always the way `Intl.NumberFormat` rounds.
 *
 * @example
 * formatFixedPercent(0.95) // "95.0%"
 * formatFixedPercent(0) // "0.0%"
 * formatFixedPercent(0.0015) // "0.1%" (Intl.NumberFormat writes "0.2%")
 */
export function formatFixedPercent(value: number, precision = 1): string {
  return `${(value * 100).toFixed(precision)}%`;
}

export function formatDecimal(value: number, decimalPlaces = 2): string {
  return String(Math.round(value * 10 ** decimalPlaces) / 10 ** decimalPlaces);
}

export type FormatNumberOptions = {
  locale?: string | string[];
  round?: boolean;
  fallback?: string;
};

/**
 * Format a number with thousands separators.
 *
 * @param value - The number or numeric string to format
 * @param options - Optional locale, rounding, and fallback
 * @returns Formatted number string (e.g., "1,234,567")
 *
 * @example
 * formatNumber(1234567) // "1,234,567"
 * formatNumber(1000) // "1,000"
 * formatNumber(undefined) // "0"
 * formatNumber(undefined, { fallback: '-' }) // "-"
 * formatNumber(1234.6, { round: true }) // "1,235"
 * formatNumber(1234, { locale: 'de-DE' }) // "1.234"
 */
export function formatNumber(value?: number | string, options: FormatNumberOptions = {}): string {
  const { locale, round = false, fallback = '0' } = options;
  if (value == null) return fallback;

  const normalized = Number(value);
  if (Number.isNaN(normalized)) return String(value);

  const rounded = round ? Math.round(normalized) : normalized;
  return new Intl.NumberFormat(locale).format(rounded);
}

export type FormatCompactNumberOptions = {
  /** Fraction-digit cap for the compact output. Defaults to 0 (whole numbers, e.g. "2M"). */
  maximumFractionDigits?: number;
  /** Returned instead of formatting when the value is NaN. Without it, NaN formats as "NaN". */
  fallback?: string;
};

/**
 * Format a number in compact notation (e.g. "1K", "3M").
 *
 * @example
 * formatCompactNumber(1234) // "1K"
 * formatCompactNumber(50) // "50"
 * formatCompactNumber(1_250_000, { maximumFractionDigits: 10 }) // "1.25M"
 * formatCompactNumber(NaN, { fallback: '--' }) // "--"
 */
export function formatCompactNumber(value: number, options: FormatCompactNumberOptions = {}): string {
  const { maximumFractionDigits = 0, fallback } = options;
  if (fallback != null && Number.isNaN(value)) {
    return fallback;
  }
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits }).format(value);
}

/**
 * Format a count with thousands separators and pluralize the accompanying noun.
 *
 * @example
 * formatNumberAndPluralize(1, 'profile') // "1 profile"
 * formatNumberAndPluralize(5000, 'profile') // "5,000 profiles"
 */
/** SI prefixes by power of a thousand, as d3's `s` format names them. */
const SI_PREFIXES = ['', 'k', 'M', 'G', 'T', 'P', 'E'];

/**
 * Ember's `shortenNumber` helper: the grouped number ("999") while it is under five characters,
 * else three significant digits with an SI prefix, as d3's `.3s` writes them ("1.23k", "12.3k",
 * "123k", "1.23M", "1.00G"). The length counts the grouping comma, as Ember's does, so every value
 * from 1,000 up is shortened. `--` for an absent number; `null` reads as 0, as d3 coerces it.
 */
export function formatShortNumber(value: number | null | undefined): string {
  if (value === undefined || Number.isNaN(value)) {
    return '--';
  }
  if (value === null) {
    return '0';
  }
  const grouped = value.toLocaleString('en-US');
  if (grouped.length < 5) {
    return grouped;
  }
  // Rounded first, so a value that rounds up to the next prefix ("999,999" → "1.00M") takes it.
  const rounded = Number(Math.abs(value).toExponential(2));
  const exponent = Math.floor(Math.log10(rounded));
  const group = Math.min(Math.floor(exponent / 3), SI_PREFIXES.length - 1);
  const scaled = rounded / 10 ** (group * 3);
  const sign = value < 0 ? '-' : '';
  return `${sign}${scaled.toFixed(Math.max(0, 2 - (exponent - group * 3)))}${SI_PREFIXES[group]}`;
}

/** Ember's `pct` helper: a 0–1 fraction as a one-decimal percentage, `--` when there is none. */
export function formatRatePercent(value: number | null | undefined): string {
  return value == null || Number.isNaN(value) ? '--' : formatFixedPercent(value, 1);
}

export function formatNumberAndPluralize(value: number, word: string): string {
  return `${formatNumber(value)} ${plur(word, value)}`;
}

/** Holds a value inside an inclusive range. */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
